import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import {
  FileText,
  Download,
  BarChart3,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react'

interface Framework {
  id: string
  name: string
  display_name: string
}

interface ComplianceSummary {
  framework_id: string
  framework_name: string
  framework_code: string
  total_controls: number
  compliant: number
  non_compliant: number
  in_progress: number
  pending: number
  not_applicable: number
  compliance_rate: number
}

export default function Reports() {
  const [frameworks, setFrameworks] = useState<Framework[]>([])
  const [summary, setSummary] = useState<ComplianceSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedFramework, setSelectedFramework] = useState('')
  const [exporting, setExporting] = useState<string | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    setLoading(true)
    try {
      const [fwRes, summaryRes] = await Promise.all([
        api.getFrameworks(),
        api.getComplianceSummary(),
      ])
      if (fwRes.success) {
        setFrameworks(fwRes.frameworks)
      }
      if (summaryRes.success && summaryRes.report) {
        setSummary(summaryRes.report.summary || [])
      }
    } catch (err) {
      console.error('Failed to load data:', err)
    } finally {
      setLoading(false)
    }
  }

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const handleExportControls = async () => {
    setExporting('controls')
    try {
      const result = await api.exportControls({ format: 'csv', framework_id: selectedFramework || undefined })
      if (result instanceof Blob) {
        downloadBlob(result, `controls-${new Date().toISOString().split('T')[0]}.csv`)
      } else {
        // JSON response - convert to CSV or handle as needed
        console.log('Export data:', result)
      }
    } catch (err) {
      console.error('Failed to export controls:', err)
    } finally {
      setExporting(null)
    }
  }

  const handleExportEvidence = async () => {
    setExporting('evidence')
    try {
      const result = await api.exportEvidence({ format: 'csv' })
      if (result instanceof Blob) {
        downloadBlob(result, `evidence-${new Date().toISOString().split('T')[0]}.csv`)
      } else {
        console.log('Export data:', result)
      }
    } catch (err) {
      console.error('Failed to export evidence:', err)
    } finally {
      setExporting(null)
    }
  }

  const handleExportRisks = async () => {
    setExporting('risks')
    try {
      const blob = await api.exportRiskRegister()
      downloadBlob(blob, `risk-register-${new Date().toISOString().split('T')[0]}.csv`)
    } catch (err) {
      console.error('Failed to export risks:', err)
    } finally {
      setExporting(null)
    }
  }

  const handleExportComplianceSummary = async () => {
    setExporting('compliance')
    try {
      const token = localStorage.getItem('grc_token')
      const params = new URLSearchParams()
      if (selectedFramework) params.set('framework_id', selectedFramework)
      params.set('format', 'csv')

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://grc-tenant-dev.port-technology-account.workers.dev'}/api/reports/compliance-summary?${params}`,
        {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        }
      )
      const blob = await response.blob()
      downloadBlob(blob, `compliance-summary-${new Date().toISOString().split('T')[0]}.csv`)
    } catch (err) {
      console.error('Failed to export compliance summary:', err)
    } finally {
      setExporting(null)
    }
  }

  const getTotalCompliance = () => {
    const totals = summary.reduce(
      (acc, fw) => ({
        total: acc.total + fw.total_controls,
        compliant: acc.compliant + fw.compliant,
        nonCompliant: acc.nonCompliant + fw.non_compliant,
        inProgress: acc.inProgress + fw.in_progress,
        pending: acc.pending + fw.pending,
      }),
      { total: 0, compliant: 0, nonCompliant: 0, inProgress: 0, pending: 0 }
    )
    return totals
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  const totals = getTotalCompliance()
  const overallRate = totals.total > 0
    ? Math.round((totals.compliant / (totals.total - summary.reduce((a, f) => a + f.not_applicable, 0))) * 100)
    : 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-500">Generate and export compliance reports</p>
        </div>
      </div>

      {/* Overall Compliance Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Overall Compliance</p>
              <p className="text-3xl font-bold text-gray-900">{overallRate}%</p>
            </div>
            <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-primary-600" />
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Compliant</p>
              <p className="text-3xl font-bold text-green-600">{totals.compliant}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Non-Compliant</p>
              <p className="text-3xl font-bold text-red-600">{totals.nonCompliant}</p>
            </div>
            <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">In Progress</p>
              <p className="text-3xl font-bold text-yellow-600">{totals.inProgress + totals.pending}</p>
            </div>
            <div className="h-12 w-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Framework Compliance Summary */}
      <div className="card">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Compliance by Framework</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Framework
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Controls
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compliant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Non-Compliant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  In Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compliance Rate
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {summary.map((fw) => (
                <tr key={fw.framework_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Shield className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {fw.framework_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {fw.total_controls}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-green-600 font-medium">{fw.compliant}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-red-600 font-medium">{fw.non_compliant}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-yellow-600 font-medium">
                      {fw.in_progress + fw.pending}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full max-w-[100px]">
                        <div
                          className="h-2 bg-green-500 rounded-full"
                          style={{ width: `${fw.compliance_rate || 0}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {fw.compliance_rate || 0}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Export Reports */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Export Reports</h2>

        <div className="mb-4">
          <label className="label">Filter by Framework (optional)</label>
          <select
            value={selectedFramework}
            onChange={(e) => setSelectedFramework(e.target.value)}
            className="input max-w-xs"
          >
            <option value="">All Frameworks</option>
            {frameworks.map((fw) => (
              <option key={fw.id} value={fw.id}>
                {fw.display_name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={handleExportComplianceSummary}
            disabled={exporting === 'compliance'}
            className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <div className="flex items-center">
              <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                <BarChart3 className="h-5 w-5 text-primary-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">Compliance Summary</p>
                <p className="text-xs text-gray-500">CSV format</p>
              </div>
            </div>
            <Download className="h-5 w-5 text-gray-400" />
          </button>

          <button
            onClick={handleExportControls}
            disabled={exporting === 'controls'}
            className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <div className="flex items-center">
              <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">Controls List</p>
                <p className="text-xs text-gray-500">CSV format</p>
              </div>
            </div>
            <Download className="h-5 w-5 text-gray-400" />
          </button>

          <button
            onClick={handleExportEvidence}
            disabled={exporting === 'evidence'}
            className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <div className="flex items-center">
              <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">Evidence Export</p>
                <p className="text-xs text-gray-500">CSV format</p>
              </div>
            </div>
            <Download className="h-5 w-5 text-gray-400" />
          </button>

          <button
            onClick={handleExportRisks}
            disabled={exporting === 'risks'}
            className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <div className="flex items-center">
              <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">Risk Register</p>
                <p className="text-xs text-gray-500">CSV format</p>
              </div>
            </div>
            <Download className="h-5 w-5 text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  )
}
